import {
    EngineConfig,
    RuntimeFrame,
    AnimationModeConfig,
    ParsedFrame,
} from './AnimationTypes';
import {
    evaluateEngineInitializationBounds,
    calculateSmartUserQualityOptions,
} from './AnimationConfigPipeline';
import { initializeLoadingPipeline } from './PipelineLoaderSegment';
import {
    performMatrixTransformDraw,
    executeFrameBlendLayers,
} from './CanvasRendererSegment';
import { calculatePerfectCanvasDimensions } from './AnimationUtilities';
import { DeviceProfile } from '@/components/provider/UserCurrentProvider';

export class AnimationEngine {
    public nodeId: number;
    public subDir: string;
    public rawFrames: ParsedFrame[];
    public frameSize: string;
    public fallbackStrategy: 'wait' | 'loop';
    public playbackMode: AnimationModeConfig;

    // Runtime state flags
    public fps: number;
    public frameInterval: number;
    public isPlaying: boolean = false;
    public currentIndex: number = 0;
    public runtimeFrames: RuntimeFrame[] = [];
    public loadedCount: number = 0;

    // Cross Dissolve Controls
    public blendDuration: number = 180;
    public blendStartTime: number | null = null;
    public prevIndex: number | null = null;

    public performanceFrameSkip: boolean = false;
    public rafId: number | null = null;
    public lastFrameTime: number = 0;

    // Targets
    public canvas: HTMLCanvasElement | null = null;
    public ctx: CanvasRenderingContext2D | null = null;
    public stillLayer: HTMLElement | null = null;
    public progressBar: HTMLProgressElement | null = null;
    public isCanvasSized: boolean = false;
    public isWaitingToPauseFinish: boolean = false;
    public alignmentIndex: number = 0;

    public loadingStartTime: number = performance.now();
    public initialDesiredFps: number = 12;

    public deviceProfile: DeviceProfile;

    constructor(config: EngineConfig) {
        this.nodeId = config.nodeId;
        this.subDir = config.subDir;
        this.rawFrames = config.frames;
        this.fallbackStrategy = config.fallbackStrategy || 'wait';
        this.playbackMode = config.playbackMode;
        this.deviceProfile = config.deviceProfile;

        // Static configuration setup evaluation pass
        const setup = evaluateEngineInitializationBounds(
            config.playbackMode,
            config.deviceProfile,
        );
        this.fps = setup.initialFps;
        this.frameSize = setup.frameSize;
        this.performanceFrameSkip = setup.frameSkip;

        this.initialDesiredFps = this.fps;
        this.frameInterval = 1000 / this.fps;
        this.alignmentIndex = config.alignmentIndex ?? 0;

        this.recalculateBlendCurve();

        this.stillLayer = document.getElementById(
            `AnimationStill_${this.nodeId}`,
        );
        this.canvas = document.getElementById(
            `AnimationCanvas_${this.nodeId}`,
        ) as HTMLCanvasElement;
        this.progressBar = document.getElementById(
            `AnimationProgress_${this.nodeId}`,
        ) as HTMLProgressElement;

        if (this.canvas) {
            this.ctx = this.canvas.getContext('2d');

            // Define your stable base reference resolution based on mode type
            const targetRefWidth =
                this.playbackMode.modeType === 'SLIDESHOW' ? 2400 : 1200;
            const targetRefHeight =
                this.playbackMode.modeType === 'SLIDESHOW' ? 1800 : 900; // Adjust to match your expected ratio

            // Calculate the perfect canvas dimensions using the stable baseline
            const perfectDim = calculatePerfectCanvasDimensions(
                this.rawFrames,
                targetRefWidth,
                targetRefHeight,
                this.alignmentIndex,
            );

            this.canvas.width = perfectDim.width;
            this.canvas.height = perfectDim.height;
            this.isCanvasSized = true;
        }

        // Delegate initialization sequence tasks cleanly to segment imports
        initializeLoadingPipeline(this);
    }

    public setAlignmentIndex(index: number): void {
        this.alignmentIndex = index;
        if (this.canvas) {
            // Re-evaluate dimensions based on new alignment bounds
            const targetRefWidth =
                this.playbackMode.modeType === 'SLIDESHOW' ? 2400 : 1200;
            const targetRefHeight =
                this.playbackMode.modeType === 'SLIDESHOW' ? 1800 : 900;
            const perfectDim = calculatePerfectCanvasDimensions(
                this.rawFrames,
                targetRefWidth,
                targetRefHeight,
                this.alignmentIndex,
            );
            this.canvas.width = perfectDim.width;
            this.canvas.height = perfectDim.height;
        }
        this.renderFrameToCanvas(this.currentIndex);
    }

    /**
     * Evaluates core frame-rate interval steps to establish structural
     * blend configurations based on post-production industry standards.
     */
    public recalculateBlendCurve(): void {
        if (!this.playbackMode.allowCrossDissolve) {
            this.blendDuration = 0;
            return;
        }

        // Fast target playback (>= 10 FPS / <= 100ms intervals) runs with crisp hard cuts
        if (this.frameInterval <= 100) {
            this.blendDuration = 0;
        }
        // Medium speeds (between 10 FPS and 2 FPS) inject a 15% shutter-blur transition
        else if (this.frameInterval <= 500) {
            this.blendDuration = this.frameInterval * 0.15;
        }
        // Slow slideshow speeds (< 2 FPS) snap cleanly onto a fixed cinematic 150ms fade window
        else {
            this.blendDuration = 150;
        }
    }

    public updateFps(newFps: number) {
        this.fps = newFps;
        this.initialDesiredFps = newFps;
        this.frameInterval = 1000 / this.fps;
        this.recalculateBlendCurve();
    }

    public changeResolution(newSize: string) {
        // Hard terminate any lingering rendering loops instantly
        this.isPlaying = false;
        this.isWaitingToPauseFinish = false;

        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this.blendStartTime = null;
        this.prevIndex = null;

        let resolvedSize = newSize;

        if (newSize === 'low' || newSize === 'auto' || newSize === 'best') {
            const userChoices = calculateSmartUserQualityOptions(
                this.playbackMode,
                this.deviceProfile,
            );
            const matchedProfile = userChoices.find(
                (choice) => choice.value === newSize,
            );
            resolvedSize = matchedProfile ? (matchedProfile.id as string) : 'm';
        }

        this.currentIndex = 0;

        this.frameSize = resolvedSize;
        this.loadedCount = 0;
        this.isCanvasSized = false;
        this.loadingStartTime = performance.now();

        this.runtimeFrames.forEach((f) => {
            f.image = null;
        });
        this.runtimeFrames = [];

        // Reset the UI progress track box back to loading mode states
        const id = this.nodeId;
        const progressBarEl = document.getElementById(
            `AnimationProgressBar_${id}`,
        );
        if (progressBarEl) {
            progressBarEl.classList.remove('isLoaded');
            progressBarEl.classList.add('isVisible');
            const fillNode = progressBarEl.querySelector(
                '.Animation_progressBar_fill',
            ) as HTMLElement;
            if (fillNode) fillNode.style.width = '0%';
        }

        // Fire off the image loaders to populate the fresh size profile array
        initializeLoadingPipeline(this);

        // 💡 REMOVED: All manual document.getElementById playBtn hacks and this.play() calls are gone!
    }

    public sizeCanvasToLoadedAsset(loadedImg: HTMLImageElement) {
        if (!this.canvas || !this.ctx || this.isCanvasSized) return;

        // Pass the real loaded asset pixel properties straight to our exact geometry evaluator
        const perfectDim = calculatePerfectCanvasDimensions(
            this.rawFrames,
            loadedImg.width,
            loadedImg.height,
        );

        this.canvas.width = perfectDim.width;
        this.canvas.height = perfectDim.height;
        this.isCanvasSized = true;
    }

    public togglePlay(): boolean {
        if (this.isPlaying) this.pause();
        else this.play();
        return this.isPlaying;
    }

    public play() {
        if (this.isPlaying) return;
        this.isPlaying = true;

        if (
            this.stillLayer &&
            !this.stillLayer.classList.contains('isHidden')
        ) {
            this.stillLayer.classList.add('isHidden');
        }

        this.lastFrameTime = performance.now();
        this.rafId = requestAnimationFrame((ts) => this.engineLoop(ts));
    }

    public pause() {
        if (this.blendStartTime !== null && this.prevIndex !== null) {
            this.isWaitingToPauseFinish = true;
            return;
        }

        this.isPlaying = false;
        this.isWaitingToPauseFinish = false;
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this.blendStartTime = null;
    }

    public stepFrame(direction: number) {
        this.pause();

        if (
            this.stillLayer &&
            !this.stillLayer.classList.contains('isHidden')
        ) {
            this.stillLayer.classList.add('isHidden');
        }

        const total = this.runtimeFrames.length;
        const step = this.performanceFrameSkip ? 2 : 1;

        let target = this.currentIndex + direction * step;
        if (this.performanceFrameSkip) {
            // Force target to an even index
            target = target - (Math.abs(target) % 2);
            if (target >= total) target = 0;
            if (target < 0) target = total % 2 === 0 ? total - 2 : total - 1;
        } else {
            target = target % total;
            if (target < 0) target += total;
        }

        if (this.runtimeFrames[target]?.isReady) {
            this.currentIndex = target;
            this.prevIndex = null;
            this.renderFrameToCanvas(this.currentIndex);
        }
    }

    private engineLoop(timestamp: number) {
        if (!this.isPlaying) return;

        const elapsed = timestamp - this.lastFrameTime;

        if (elapsed >= this.frameInterval) {
            this.lastFrameTime = timestamp - (elapsed % this.frameInterval);
            this.advanceFrame();
        }

        executeFrameBlendLayers(this, timestamp);
        this.rafId = requestAnimationFrame((ts) => this.engineLoop(ts));
    }

    private advanceFrame() {
        const total = this.runtimeFrames.length;
        const step = this.performanceFrameSkip ? 2 : 1;
        let nextIndex = this.currentIndex + step;

        // When performanceFrameSkip is active, stay on even indices (0, 2, 4...)
        if (this.performanceFrameSkip) {
            if (nextIndex >= total) {
                nextIndex = 0; // Wrap back cleanly to frame 0
            }
        } else {
            nextIndex = nextIndex % total;
        }

        const nextFrame = this.runtimeFrames[nextIndex];
        const isFrameReadyToPaint =
            nextFrame &&
            nextFrame.isReady &&
            nextFrame.image &&
            nextFrame.image.complete &&
            nextFrame.image.naturalWidth > 0;

        if (!isFrameReadyToPaint) {
            if (this.fallbackStrategy === 'wait') {
                if (this.loadedCount < total) return;
                this.ensureFrameLoaded(nextIndex);
                return;
            } else {
                nextIndex = 0;
            }
        }

        this.prevIndex = this.currentIndex;
        this.currentIndex = nextIndex;
        this.ensureFrameLoaded(this.currentIndex);
        this.blendStartTime = performance.now();
    }

    /**
     * Ensures target frame image is in memory (from disk cache)
     * and trims distant frames to protect mobile WebKit memory limits.
     */
    public ensureFrameLoaded(index: number): void {
        const frame = this.runtimeFrames[index];
        if (!frame) return;

        // Hydrate frame if evicted
        if (!frame.image && frame.isReady) {
            const img = new Image();
            img.src = frame.url;
            frame.image = img;
        }

        // Hydrate next 2 frames ahead for smooth cross-dissolves
        const step = this.performanceFrameSkip ? 2 : 1;
        const total = this.runtimeFrames.length;
        for (let i = 1; i <= 2; i++) {
            const aheadIdx = (index + i * step) % total;
            const aheadFrame = this.runtimeFrames[aheadIdx];
            if (aheadFrame && !aheadFrame.image && aheadFrame.isReady) {
                const aheadImg = new Image();
                aheadImg.src = aheadFrame.url;
                aheadFrame.image = aheadImg;
            }
        }

        // Evict frames far behind playback position (keep window of ~10 frames)
        const evictIdx = (index - 10 * step + total) % total;
        const evictFrame = this.runtimeFrames[evictIdx];
        if (
            evictFrame &&
            evictFrame.image &&
            evictIdx !== this.currentIndex &&
            evictIdx !== this.prevIndex
        ) {
            evictFrame.image.src = '';
            evictFrame.image = null;
        }
    }

    public renderFrameToCanvas(idx: number) {
        if (!this.canvas || !this.ctx) return;
        this.ensureFrameLoaded(idx);
        const target = this.runtimeFrames[idx];

        if (!target || !target.isReady || !target.image) return;

        const align =
            target.meta.alignments[this.alignmentIndex] ||
            target.meta.alignments[0];

        // Apply Pitch (rotateX) & Yaw (rotateY) directly as CSS 3D transform on <canvas>
        if (align) {
            this.canvas.style.transform = `perspective(1000px) rotateX(${align.pitch}deg) rotateY(${align.yaw}deg)`;
        }

        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        performMatrixTransformDraw(this.ctx, this.canvas, target, 1.0, this);
    }

    public destroy() {
        this.pause();
        this.runtimeFrames.forEach((f) => {
            f.image = null;
        });
        this.runtimeFrames = [];
        if (this.stillLayer) {
            this.stillLayer.classList.remove('isHidden');
        }
    }
}
